import { Col, Input, InputRef, Row } from 'antd'
import { get, map } from 'lodash'
import React, { FC } from 'react'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Field, WrappedFieldInputProps, WrappedFieldMetaProps, WrappedFieldProps } from 'redux-form'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { formFieldID, TELEPHONE_PREFIXES } from '../../utils/helpers'
import SelectField from './SelectField'

declare module 'react' {
	function forwardRef<T, P = {}>(
		render: (props: P, ref?: React.Ref<T>) => React.ReactElement | null
	): (props: P & React.RefAttributes<T>) => React.ReactElement | null
}

export interface IPhoneNumberField {
	name?: string
	label?: string
	className?: string
	formName: string
	meta: Partial<WrappedFieldMetaProps>
	input: Partial<WrappedFieldInputProps>
}

export interface IPrefixSelect {
	width?: number
	placeholder?: string
	prefixOptions?: any
	className?: string
}

const PrefixSelect: FC<IPrefixSelect & WrappedFieldProps> = ({ input, prefixOptions, placeholder, meta }) => {
	return (
		<SelectField
			input={input}
			customOptions
			onChange={(value: any) => input.onChange(value)}
			placeholder={placeholder}
			meta={meta}
			options={prefixOptions}
			defaultValue={{
				label: prefixOptions[0].label,
				value: prefixOptions[0].value,
				flag: prefixOptions[0].flag
			}}
		/>
	)
}

const NumberInput = React.forwardRef(({ input }: WrappedFieldProps, ref?: React.ForwardedRef<InputRef>) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let { value } = e.target
		value = value.replace(/\D/g, '')
		if (value.startsWith('0')) {
			value = value.substring(1)
		}
		if (value.length > 0) {
			const groups = value.match(/.{1,3}/g) || []
			value = groups.join(' ')
		}
		input.onChange(value)
	}

	return (
		<Input
			className={'text-17 form-field p-4 bg-transparent rounded-sm border-transparent focus:border-transparent'}
			{...input}
			style={{ width: '100%' }}
			placeholder={'--- --- ---'}
			onChange={handleChange}
			ref={ref}
			maxLength={11} // 9 digits + 2 spaces
		/>
	)
})

const PhoneNumberField = React.forwardRef(({ label, formName, meta, input }: IPhoneNumberField) => {
	const { form = 'form', error = undefined, touched = undefined } = meta
	const isMobile = useMediaQuery({ maxWidth: 991 })
	const { name = 'field' } = input
	const phoneNumberValue = useSelector((state) => get(state, `form.${formName}.values.phoneNumber`))
	const id = formFieldID(form, name)

	const prefixOptions = map(TELEPHONE_PREFIXES, (country) => ({
		label: country.prefix,
		value: country.prefix,
		flag: country.flag
	}))

	return (
		<div className={'mb-[16px]'}>
			{label && (
				<label htmlFor={id} className={'form-field-label'}>
					{label}
				</label>
			)}
			<Row className={`bg-gray-100 rounded-sm phone_number border border-solid ${error && touched && 'error'}`}>
				<Col sm={isMobile ? undefined : 6} span={isMobile ? undefined : 8} style={{ borderRight: '1px solid #d7d7d7' }}>
					<Field name={'phonePrefix'} component={PrefixSelect} phoneNumberTouched={phoneNumberValue} prefixOptions={prefixOptions} />
				</Col>
				<Col sm={isMobile ? undefined : 18} span={isMobile ? undefined : 16} className={'flex flex-row items-center'}>
					<Field name={'phoneNumber'} component={NumberInput} />
					<span className={'form-field-item-suffix'}>{error && touched && <ErrorIcon />}</span>
				</Col>
			</Row>
			{error && touched && <span className={'form-field-message'}>{error}</span>}
		</div>
	)
})

export default PhoneNumberField
